import { useEffect, useId, useState } from 'react';
import { endpoints } from '@/api/endpoints';
import { useDebounced } from '@/api/useDebounced';
import { useT } from '@/i18n/LanguageContext';
import type { CodeSuggestion } from '@/api/types';

export type CodeValue = { readonly code: string; readonly auto: boolean };

const CODE_FORMAT = /^[A-Z0-9][A-Z0-9-]{0,23}$/;

/**
 * ADR-083 — the "Code" field for a new warehouse or seller. Nobody should
 * have to invent a technical identifier: the code is suggested from the name
 * (server-side, so it is always free and valid), and stays automatic until
 * the user types over it. A typed code is checked (format + uniqueness)
 * before submit, with a free alternative offered. The server re-decides at
 * creation time, so this is guidance, never the guard.
 *
 * The label stays the plain word "Code" (existing screens and tests find the
 * field by it); the explanation is a hint wired through aria-describedby.
 */
export function CodeField({
  entity,
  name,
  value,
  onChange,
  disabled = false,
  id,
}: {
  entity: 'WAREHOUSE' | 'SELLER';
  /** The name typed in the same form - the suggestion follows it while the code is automatic. */
  name: string;
  value: CodeValue;
  onChange: (v: CodeValue) => void;
  disabled?: boolean;
  id?: string;
}): JSX.Element {
  const t = useT();
  const autoId = useId();
  const inputId = id ?? `code-${autoId}`;
  const hintId = `${inputId}-hint`;
  const statusId = `${inputId}-status`;
  const debouncedName = useDebounced(name, 300);
  const debouncedCode = useDebounced(value.auto ? '' : value.code, 300);
  const [check, setCheck] = useState<CodeSuggestion | null>(null);

  useEffect(() => {
    let cancelled = false;
    endpoints
      .suggestCode({ entity, name: debouncedName, ...(debouncedCode ? { code: debouncedCode } : {}) })
      .then((r) => {
        if (cancelled) return;
        setCheck(r);
        if (value.auto && r.suggested !== value.code) onChange({ code: r.suggested, auto: true });
      })
      .catch(() => {
        /* suggestion is a convenience: the server still decides on submit */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, debouncedName, debouncedCode, value.auto]);

  const typed = value.code.trim().toUpperCase();
  const formatOk = typed === '' || CODE_FORMAT.test(typed);
  const req = !value.auto && check?.requested?.code === typed ? check.requested : null;
  let status: { tone: 'ok' | 'warn' | 'muted'; text: string } | null = null;
  if (value.auto) status = value.code ? { tone: 'muted', text: t('code.autoNote') } : null;
  else if (!formatOk) status = { tone: 'warn', text: t('code.formatHint') };
  else if (req && !req.available) status = { tone: 'warn', text: t('code.taken', { suggestion: check?.suggested ?? '' }) };
  else if (req && req.available) status = { tone: 'ok', text: t('code.free') };

  return (
    <div className="field-row">
      <label htmlFor={inputId}>{t('billing.colCode')}</label>
      <div className="code-field">
        <input
          id={inputId}
          name="code"
          value={value.code}
          disabled={disabled}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          maxLength={24}
          aria-describedby={`${hintId} ${statusId}`}
          aria-invalid={!formatOk || (req !== null && !req.available)}
          onChange={(e) => onChange({ code: e.target.value.toUpperCase(), auto: false })}
        />
        {!value.auto && (
          <button type="button" className="link-button" disabled={disabled} onClick={() => onChange({ code: check?.suggested ?? '', auto: true })}>
            {t('code.useSuggested')}
          </button>
        )}
      </div>
      <p id={hintId} className="field-hint">
        {entity === 'WAREHOUSE' ? t('code.hintWarehouse') : t('code.hintSeller')}
      </p>
      <p id={statusId} className={`field-hint ${status?.tone ?? ''}`} aria-live="polite">
        {status?.text ?? ''}
      </p>
    </div>
  );
}

/** What to send: an automatic code lets the server allocate (race-safe); a typed one is sent as typed. */
export function codePayload(v: CodeValue): { code?: string; codeAuto?: boolean } {
  return v.auto || v.code.trim() === '' ? { codeAuto: true } : { code: v.code.trim().toUpperCase() };
}
